import React from 'react';
import { DEFAULT_PROCESSING_FEE_CONTRIBUTION_LABEL, formatTaxLineLabel, getFeeLabel } from '@repo/lib';
import { formatPrice } from '../services/formatPrice';
import useCartCost, { checkIsTaxEnabled } from '../hooks/useCartCost';
import getFixedTaxRate from '../lib/getFixedTaxRate';
import useCheckoutState from '../hooks/useCheckoutState';
import useAiTarget from '../hooks/useAiTarget';
import useSelectedPrice from '../hooks/useSelectedPrice';
import { isRecurringPurchase, getEffectiveInterval } from '../lib/multiPrice';
import { PAGE_TYPES } from '../enums/page-types';

import { upperFirst } from 'lodash';
import checkHasValidStartDate from '../lib/checkHasValidStartDate';
import formatStartDate from '../lib/formatStartDate';
import checkHasTrialPeriod from '../lib/checkHasTrialPeriod';
import getPriceBillingConfig from '../lib/getPriceBillingConfig';
import { getVatLocale } from '../lib/locale/vat-locale';
import checkShouldShowPriceBreakdown from '../lib/checkShouldShowPriceBreakdown';
import clsx from 'clsx';
import { InfoTooltip } from './Tooltip';
import { useCheckoutLocalization } from '@repo/checkout/hooks/useCheckoutLocalization';
import getThemeLabels from '../lib/getThemeLabels';
import getSubscriptionIntervalWithSlash from '../services/translation/getSubscriptionIntervalWithSlash';
import StyledElement from '@repo/checkout/components/StyledElement';
import { checkIsTicketTypeCoupon } from '../lib/checkCoupon';

const feeLabel = (fee, currency, locale) =>
  getFeeLabel(fee, amount => formatPrice({ amount, currency, locale, style: 'currency' }));

function PriceBreakdownContainer({ children }) {
  return (
    <StyledElement
      elementPath="checkout.form.priceBreakdown"
      as="div"
      className={clsx(
        'cp_price-breakdown-wrapper',
        'flex flex-grow flex-col justify-end space-y-0.5',
        'bg-[var(--cp_checkout-form-priceBreakdown-background-color)]',
        'text-[var(--cp_checkout-form-priceBreakdown-color)]',
        'text-[var(--cp_checkout-form-priceBreakdown-color)]',
        '[font-size:var(--cp_checkout-form-priceBreakdown-font-size)]',
        '[line-height:var(--cp_checkout-form-priceBreakdown-line-height)]',
        '[font-weight:var(--cp_checkout-form-priceBreakdown-font-weight)]',
        '[align-items:var(--cp_checkout-form-priceBreakdown-items-align, end)]',
      )}
    >
      {children}
    </StyledElement>
  );
}

const AI_TARGET_BY_ROW_TYPE = {
  fee: 'fees',
  fees: 'fees',
  tax: 'tax',
  total: 'total',
  dueNow: 'total',
  bulkDiscount: 'bulkDiscounts',
};

function PriceBreakdownRow({ children, type }) {
  const aiTarget = useAiTarget();
  const target = AI_TARGET_BY_ROW_TYPE[type];

  return (
    <StyledElement
      elementPath={`checkout.form.priceBreakdown.${type}`}
      {...(target && aiTarget(target))}
      className={clsx(
        `cp_price-breakdown-item cp_price-breakdown-item--${type}`,
        'flex gap-x-1 items-end',
      )}
      style={{
        color: `var(--cp_checkout-form-priceBreakdown-${type}-color)`,
        justifyContent: 'var(--cp_checkout-form-priceBreakdown-justify, end)',
        fontSize: `var(--cp_checkout-form-priceBreakdown-${type}-font-size, var(--cp_checkout-form-priceBreakdown-font-size))`,
        lineHeight: `var(--cp_checkout-form-priceBreakdown-${type}-line-height, var(--cp_checkout-form-priceBreakdown-line-height))`,
        fontWeight: `var(--cp_checkout-form-priceBreakdown-${type}-font-weight, var(--cp_checkout-form-priceBreakdown-font-weight))`,
        fontFamily: `var(--cp_checkout-form-priceBreakdown-${type}-font-family, var(--cp_checkout-form-priceBreakdown-font-family))`,
        fontStyle: `var(--cp_checkout-form-priceBreakdown-${type}-font-style, var(--cp_checkout-form-priceBreakdown-font-style))`,
      }}
    >
      {children}
    </StyledElement>
  );
}

function PriceBreakdownLabel({ label, type }) {
  if (typeof label === 'string') {
    return (
      <span className={clsx(`cp_price-breakdown-label cp_price-breakdown-label--${type}`)}>
        {upperFirst(label)}:{' '}
      </span>
    );
  }

  return (
    <span className={clsx(`cp_price-breakdown-label cp_price-breakdown-label--${type}`)}>
      {label}
    </span>
  );
}

function PriceBreakdownValue({ children, type }) {
  const { isLoading } = useCartCost();

  return (
    <span
      className={clsx('cp_price-breakdown-value', `cp_price-breakdown-value--${type}`, {
        'blur-[4px]': isLoading,
      })}
    >
      {children}
    </span>
  );
}

// Seller-typed names keep their casing ("iPhone case"); only theme labels go
// through upperFirst.
function itemLabel(name) {
  return <>{name}: </>;
}

// The primary gets its own rows only once a bump joins the subtotal: a bump-free
// checkout keeps its single subtotal, and events itemise their tickets instead.
function checkItemisesPrimary(page, cartCost) {
  return (
    page.type === PAGE_TYPES.CHECKOUT &&
    (cartCost.lineItems || []).some(item => item.type === 'order_bump')
  );
}

// Once itemised, the primary's setup fee sits under its row like a bump's, so
// the subtotal takes it in too.
function getItemisedPrimarySetupFeeAmount(page, cartCost) {
  return checkItemisesPrimary(page, cartCost) ? cartCost.setupFeeAmount || 0 : 0;
}

function MainProductRow() {
  const cartCost = useCartCost();
  const { page, product, currencyLocale, theme } = useCheckoutState();
  const selectedPrice = useSelectedPrice();

  if (!checkItemisesPrimary(page, cartCost)) return null;

  const labels = getThemeLabels(theme);
  // A payment plan's subtotal is the whole plan, so it carries no interval.
  const interval =
    isRecurringPurchase(selectedPrice, product) && !(cartCost.planIterations >= 1)
      ? getEffectiveInterval(selectedPrice, product)
      : null;

  return (
    <PriceBreakdownRow type="product">
      <PriceBreakdownLabel
        label={itemLabel(
          cartCost.quantity > 1 ? `${product.title} × ${cartCost.quantity}` : product.title,
        )}
        type="product"
      />
      <PriceBreakdownValue type="product">
        {formatPrice({
          amount: cartCost.primarySubtotalAmount - sumPrimaryOneTimeVariants(cartCost),
          currency: cartCost.currency,
          locale: currencyLocale,
          style: 'currency',
        })}
        {interval
          ? ` ${getSubscriptionIntervalWithSlash(interval.interval, interval.intervalCount ?? 1, labels)}`
          : ''}
      </PriceBreakdownValue>
    </PriceBreakdownRow>
  );
}

// A payment plan's primary row is the whole plan with no interval, so it keeps this money.
function getPrimaryOneTimeVariants(cartCost) {
  return cartCost.planIterations >= 1 ? [] : cartCost.primaryOneTimeVariants || [];
}

function sumPrimaryOneTimeVariants(cartCost) {
  return getPrimaryOneTimeVariants(cartCost).reduce((sum, line) => sum + line.amount, 0);
}

// One-time variant money is due once, so on a subscription it leaves the
// primary's interval row for rows of its own.
function PrimaryOneTimeVariantRows() {
  const cartCost = useCartCost();
  const { page, currencyLocale } = useCheckoutState();

  if (!checkItemisesPrimary(page, cartCost)) return null;

  return getPrimaryOneTimeVariants(cartCost).map(line => (
    <PriceBreakdownRow key={line.name} type="product">
      <PriceBreakdownLabel label={itemLabel(line.name)} type="product" />
      <PriceBreakdownValue type="product">
        {formatPrice({
          amount: line.amount,
          currency: cartCost.currency,
          locale: currencyLocale,
          style: 'currency',
        })}
      </PriceBreakdownValue>
    </PriceBreakdownRow>
  ));
}

// Under the primary's row (`itemised`) it is named for the product, beside the
// bumps' named fees; under the subtotal it keeps the plain theme label.
function PrimarySetupFeeRow({ labels, itemised = false }) {
  const cartCost = useCartCost();
  const { page, product, currencyLocale } = useCheckoutState();

  if (!(cartCost.setupFeeAmount > 0)) return null;
  if (checkItemisesPrimary(page, cartCost) !== itemised) return null;

  return (
    <PriceBreakdownRow type="setupFee">
      <PriceBreakdownLabel
        label={
          itemised
            ? itemLabel(`${product.title} ${labels.setupFeeText ?? 'setup fee'}`)
            : labels.cartSetupFeeText
        }
        type="setupFee"
      />
      <PriceBreakdownValue type="setupFee">
        {formatPrice({
          amount: cartCost.setupFeeAmount,
          currency: cartCost.currency,
          locale: currencyLocale,
          style: 'currency',
        })}
      </PriceBreakdownValue>
    </PriceBreakdownRow>
  );
}

// One row per selected order bump, above the subtotal that already includes
// them — itemization, not addition.
function OrderBumpRows() {
  const cartCost = useCartCost();
  const { currencyLocale, theme } = useCheckoutState();

  const bumpItems = (cartCost.lineItems || []).filter(item => item.type === 'order_bump');

  if (!bumpItems.length) return null;

  const labels = getThemeLabels(theme);

  const money = amount =>
    formatPrice({ amount, currency: cartCost.currency, locale: currencyLocale, style: 'currency' });

  return bumpItems.map(item => (
    <React.Fragment key={item.id}>
      <PriceBreakdownRow type="orderBump">
        {/* The count only appears where a placement allows one, so an ordinary
            accept-or-decline bump reads exactly as it did. */}
        <PriceBreakdownLabel
          label={itemLabel(item.quantity > 1 ? `${item.name} × ${item.quantity}` : item.name)}
          type="orderBump"
        />
        <PriceBreakdownValue type="orderBump">
          {money(item.amount)}
          {item.billingType === 'recurring'
            ? ` ${getSubscriptionIntervalWithSlash(item.interval, item.intervalCount, labels)}`
            : ''}
        </PriceBreakdownValue>
      </PriceBreakdownRow>
      {/* The bump's setup fee sits under its bump, named for it as the email
          and invoice rows are, so two bumps with fees stay apart. The
          subtotal already holds it. */}
      {item.setupFeeAmount > 0 && (
        <PriceBreakdownRow type="setupFee">
          <PriceBreakdownLabel
            label={itemLabel(
              `${item.name} ${labels.setupFeeText ?? 'setup fee'}` +
                (item.setupFeeQuantity > 1 ? ` × ${item.setupFeeQuantity}` : ''),
            )}
            type="setupFee"
          />
          <PriceBreakdownValue type="setupFee">{money(item.setupFeeAmount)}</PriceBreakdownValue>
        </PriceBreakdownRow>
      )}
    </React.Fragment>
  ));
}

// One row per selected ticket type, above the subtotal that already includes
// them. A fee the group shows separately gets its own row under its ticket; a
// fee the group folds into the price stays folded into the ticket line,
// matching what the ticket row itself showed.
function TicketRows() {
  const cartCost = useCartCost();
  const { currencyLocale, theme } = useCheckoutState();
  const labels = getThemeLabels(theme);

  const ticketItems = (cartCost.lineItems || []).filter(
    item => item.type === 'ticket' && item.quantity > 0,
  );
  const isSeparateFee = item => item.fee && item.fee.display === 'separate';
  if (!ticketItems.length) return null;

  const money = amount =>
    formatPrice({ amount, currency: cartCost.currency, locale: currencyLocale, style: 'currency' });

  return ticketItems.map(item => {
    const separate = isSeparateFee(item);
    const lineAmount = item.subtotal + (item.fee && !separate ? item.fee.subtotal : 0);

    return (
      <React.Fragment key={item.id}>
        <PriceBreakdownRow type="ticket">
          <PriceBreakdownLabel label={itemLabel(`${item.name} × ${item.quantity}`)} type="ticket" />
          <PriceBreakdownValue type="ticket">{money(lineAmount)}</PriceBreakdownValue>
        </PriceBreakdownRow>
        {separate && (
          <PriceBreakdownRow type="ticketFee">
            <PriceBreakdownLabel
              label={`${labels.bookingFeeText} × ${item.quantity}`}
              type="ticketFee"
            />
            <PriceBreakdownValue type="ticketFee">{money(item.fee.subtotal)}</PriceBreakdownValue>
          </PriceBreakdownRow>
        )}
      </React.Fragment>
    );
  });
}

// Ticket fees are itemised above at face value, so the subtotal carries them
// before any discount, like the ticket prices it sums.
function getBreakdownSubtotalAmount(cartCost, page) {
  return (
    cartCost.subtotalAmount +
    (cartCost.ticketFeesSubtotalAmount || 0) +
    getItemisedPrimarySetupFeeAmount(page, cartCost)
  );
}

function getTaxLabel({ name, rate, labels }) {
  return formatTaxLineLabel({
    name: name || labels.cartTaxText,
    percentage: rate.percentage,
    inclusive: rate.inclusive,
    includedText: labels.cartTaxIncludedText,
  });
}

// The whole coupon a ticket type got, its fee share included, so the rows
// under the gross subtotal add up to the total due.
function getTicketCouponDiscountAmount(item) {
  return (item.couponDiscountAmount || 0) + (item.fee?.couponDiscountAmount || 0);
}

function TicketCouponDiscountBreakdown({ lineItems, currency, locale }) {
  if (!lineItems || !lineItems.length) return null;

  const itemsWithDiscount = lineItems.filter(item => getTicketCouponDiscountAmount(item) > 0);

  if (!itemsWithDiscount.length) return null;

  return itemsWithDiscount.map(item => (
    <PriceBreakdownRow key={item.id} type="discount">
      <PriceBreakdownLabel label={itemLabel(item.name)} type="discount" />
      <PriceBreakdownValue type="discount">
        -
        {formatPrice({
          amount: getTicketCouponDiscountAmount(item),
          currency: currency,
          locale: locale,
          style: 'currency',
        })}
      </PriceBreakdownValue>
    </PriceBreakdownRow>
  ));
}

function ProcessingFeeContributionRow({ labels }) {
  const cartCost = useCartCost();
  const { currencyLocale } = useCheckoutState();

  if (!cartCost.processingFeeContribution) return null;

  return (
    <PriceBreakdownRow type="processingFeeContribution">
      <PriceBreakdownLabel
        label={labels.processingFeeContributionText || DEFAULT_PROCESSING_FEE_CONTRIBUTION_LABEL}
        type="processingFeeContribution"
      />
      <PriceBreakdownValue type="processingFeeContribution">
        {formatPrice({
          amount: cartCost.processingFeeContribution.amount,
          currency: cartCost.currency,
          locale: currencyLocale,
          style: 'currency',
        })}
      </PriceBreakdownValue>
    </PriceBreakdownRow>
  );
}

export default function PriceBreakdown() {
  const cartCost = useCartCost();
  const { page, product, seller, form, theme } = useCheckoutState();
  const selectedPrice = useSelectedPrice();

  const labels = getThemeLabels(theme);

  const shouldShowPriceBreakdown = checkShouldShowPriceBreakdown({
    pageType: page.type,
    // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.type)
    productType: product.type,
    selectedPrice,
    enabledPriceCount: product.prices?.filter(p => p.enabled !== false).length ?? 0,
    cartCost: cartCost,
    productVariants: product.variants,
    formVariants: form.variants,
    pageTicketGroups: page.ticketGroups,
  });

  if (!shouldShowPriceBreakdown) return null;

  const taxEnabled = checkIsTaxEnabled({ seller, page, product });

  // The breakdown follows the buyer's selected price (legacy type fallback).
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.type via isRecurringPurchase)
  const isSubscription = isRecurringPurchase(selectedPrice, product);

  if (isSubscription) {
    if (taxEnabled) {
      return <SubscriptionTaxEnabledPriceBreakdown labels={labels} />;
    } else {
      return <SubscriptionRegularPriceBreakdown labels={labels} />;
    }
  } else {
    if (taxEnabled) {
      return <TaxEnabledPriceBreakdown labels={labels} />;
    } else {
      return <RegularPriceBreakdown labels={labels} />;
    }
  }
}

function SubscriptionTaxEnabledPriceBreakdown({ labels }) {
  const cartCost = useCartCost();
  const { page, product, currencyLocale } = useCheckoutState();
  const { locale } = useCheckoutLocalization();

  const fixedTaxRate = page.tax?.mode === 'fixed' ? getFixedTaxRate({ page, product }) : null;

  // Trial / start date follow the selected price (per-price billing config);
  // legacy products (no prices[]) keep the product-level values.
  const selectedPrice = useSelectedPrice();
  const billingConfig = getPriceBillingConfig(selectedPrice);
  const effectiveTrialPeriodDays = selectedPrice
    ? billingConfig?.trialPeriodDays
    : product.trialPeriodDays;
  const effectiveStartDate = selectedPrice ? billingConfig?.startDate : product.startDate;
  const hasTrialPeriod = checkHasTrialPeriod(effectiveTrialPeriodDays);
  const hasStartDate = checkHasValidStartDate(effectiveStartDate);
  const hasCoupon = !!cartCost.coupon;

  return (
    <PriceBreakdownContainer>
      <MainProductRow />
      <PrimaryOneTimeVariantRows />
      <PrimarySetupFeeRow labels={labels} itemised />
      <TicketRows />
      <OrderBumpRows />

      <PriceBreakdownRow type="subtotal">
        <PriceBreakdownLabel label={labels.cartSubtotalText} type="subtotal" />
        <PriceBreakdownValue type="subtotal">
          {formatPrice({
            amount: getBreakdownSubtotalAmount(cartCost, page),
            currency: cartCost.currency,
            locale: currencyLocale,
            style: 'currency',
          })}
        </PriceBreakdownValue>
      </PriceBreakdownRow>

      <PrimarySetupFeeRow labels={labels} />

      {hasCoupon && (
        <PriceBreakdownRow type="couponDiscount">
          <PriceBreakdownLabel
            label={
              cartCost.coupon.percentOff
                ? `${labels.cartCouponDiscountText} (${cartCost.coupon.percentOff}%)`
                : labels.cartCouponDiscountText
            }
            type="couponDiscount"
          />
          <PriceBreakdownValue type="couponDiscount">
            -
            {formatPrice({
              amount: cartCost.totalDiscountAmount,
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {cartCost.fees &&
        cartCost.fees.map((fee, index) => (
          <PriceBreakdownRow key={`${fee.name}-${index}`} type="fees">
            <PriceBreakdownLabel
              label={feeLabel(fee, cartCost.currency, currencyLocale)}
              type="fees"
            />
            <PriceBreakdownValue type="fees">
              {formatPrice({
                amount: fee.amount,
                currency: cartCost.currency,
                locale: currencyLocale,
                style: 'currency',
              })}
            </PriceBreakdownValue>
          </PriceBreakdownRow>
        ))}

      {!cartCost.hasProration && (
        <>
          {cartCost.totalTaxAmounts?.length
            ? cartCost.totalTaxAmounts?.map(taxAmount => {
                const taxDisplayName =
                  taxAmount.displayName &&
                  (taxAmount.taxType === 'vat' || taxAmount.taxType === 'VAT')
                    ? getVatLocale(locale || currencyLocale || 'en-US', taxAmount.displayName)
                    : taxAmount.displayName;

                return (
                  <PriceBreakdownRow key={taxAmount.displayName} type="tax">
                    <PriceBreakdownLabel
                      label={
                        <>
                          {getTaxLabel({ name: taxDisplayName, rate: taxAmount, labels })}
                          <InfoTooltip>Tax is determined by billing information</InfoTooltip>:{' '}
                        </>
                      }
                      type="tax"
                    />

                    <PriceBreakdownValue type="tax">
                      {formatPrice({
                        amount: taxAmount.amount,
                        currency: cartCost.currency,
                        locale: currencyLocale,
                        style: 'currency',
                      })}
                    </PriceBreakdownValue>
                  </PriceBreakdownRow>
                );
              })
            : (() => {
                const totalFixedTaxAmount = cartCost.recurringTaxAmount + cartCost.oneTimeTaxAmount;
                return (
                  <PriceBreakdownRow type="tax">
                    <PriceBreakdownLabel
                      label={
                        fixedTaxRate
                          ? getTaxLabel({
                              name: fixedTaxRate.displayName,
                              rate: fixedTaxRate,
                              labels,
                            })
                          : labels.cartTaxText
                      }
                      type="tax"
                    />
                    <PriceBreakdownValue type="tax">
                      {formatPrice({
                        amount: totalFixedTaxAmount,
                        currency: cartCost.currency,
                        locale: currencyLocale,
                        style: 'currency',
                      })}
                    </PriceBreakdownValue>
                  </PriceBreakdownRow>
                );
              })()}
        </>
      )}

      {cartCost.hasProration && (
        <>
          {cartCost.totalTaxAmountsBeforeProration?.length
            ? cartCost.totalTaxAmountsBeforeProration?.map(taxAmount => {
                const taxDisplayName =
                  taxAmount.displayName &&
                  (taxAmount.taxType === 'vat' || taxAmount.taxType === 'VAT')
                    ? getVatLocale(locale || currencyLocale || 'en-US', taxAmount.displayName)
                    : taxAmount.displayName;

                return (
                  <PriceBreakdownRow key={taxAmount.displayName} type="tax">
                    <PriceBreakdownLabel
                      label={
                        <>
                          {getTaxLabel({ name: taxDisplayName, rate: taxAmount, labels })}
                          <InfoTooltip>Tax is determined by billing information</InfoTooltip>:{' '}
                        </>
                      }
                      type="tax"
                    />

                    <PriceBreakdownValue type="tax">
                      {formatPrice({
                        amount: taxAmount.amount,
                        currency: cartCost.currency,
                        locale: currencyLocale,
                        style: 'currency',
                      })}
                    </PriceBreakdownValue>
                  </PriceBreakdownRow>
                );
              })
            : (() => {
                const totalFixedTaxAmount =
                  cartCost.recurringTaxAmountBeforeProration + cartCost.oneTimeTaxAmount;
                if (totalFixedTaxAmount <= 0) return null;
                return (
                  <PriceBreakdownRow type="tax">
                    <PriceBreakdownLabel
                      label={
                        fixedTaxRate
                          ? getTaxLabel({
                              name: fixedTaxRate.displayName,
                              rate: fixedTaxRate,
                              labels,
                            })
                          : labels.cartTaxText
                      }
                      type="tax"
                    />
                    <PriceBreakdownValue type="tax">
                      {formatPrice({
                        amount: totalFixedTaxAmount,
                        currency: cartCost.currency,
                        locale: currencyLocale,
                        style: 'currency',
                      })}
                    </PriceBreakdownValue>
                  </PriceBreakdownRow>
                );
              })()}
        </>
      )}

      {hasStartDate && (
        <PriceBreakdownRow type="startDate">
          <PriceBreakdownLabel
            label={`${labels.cartTotalOnStartDateText} ${formatStartDate(effectiveStartDate)}`}
            type="startDate"
          />

          <PriceBreakdownValue type="startDate">
            {formatPrice({
              amount:
                cartCost.recurringTotalAmount +
                (cartCost.recurringProcessingFeeContribution?.amount ?? 0),
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {hasTrialPeriod && (
        <PriceBreakdownRow type="trial">
          <PriceBreakdownLabel label={labels.cartTotalAfterTrialText} type="trial" />
          <PriceBreakdownValue type="trial">
            {formatPrice({
              amount:
                cartCost.recurringTotalAmount +
                (cartCost.recurringProcessingFeeContribution?.amount ?? 0),
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      <ProcessingFeeContributionRow key="processingFeeContribution" labels={labels} />

      {!cartCost.hasProration && (
        <PriceBreakdownRow type="total">
          <PriceBreakdownLabel label={labels.cartTotalDueText} type="total" />
          <PriceBreakdownValue type="total">
            {formatPrice({
              amount: cartCost.totalDueAmount,
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {cartCost.hasProration && (
        <>
          <PriceBreakdownRow type="total">
            <PriceBreakdownLabel label={labels.cartTotalDueText} type="total" />
            <PriceBreakdownValue type="total">
              {formatPrice({
                amount: cartCost.totalDueAmountBeforeProration,
                currency: cartCost.currency,
                locale: currencyLocale,
                style: 'currency',
              })}
            </PriceBreakdownValue>
          </PriceBreakdownRow>

          <PriceBreakdownRow type="proration">
            <PriceBreakdownLabel label={labels.cartTotalDueProratedAmountText} type="proration" />
            <PriceBreakdownValue type="proration">
              {formatPrice({
                amount: cartCost.totalDueAmount,
                currency: cartCost.currency,
                locale: currencyLocale,
                style: 'currency',
              })}
            </PriceBreakdownValue>
          </PriceBreakdownRow>
        </>
      )}
    </PriceBreakdownContainer>
  );
}

function SubscriptionRegularPriceBreakdown({ labels }) {
  const { page, product, currencyLocale } = useCheckoutState();
  const cartCost = useCartCost();

  const isPaymentPlan = cartCost.planIterations >= 1;
  // Trial / start date follow the selected price (per-price billing config);
  // legacy products (no prices[]) keep the product-level values.
  const selectedPrice = useSelectedPrice();
  const billingConfig = getPriceBillingConfig(selectedPrice);
  const effectiveTrialPeriodDays = selectedPrice
    ? billingConfig?.trialPeriodDays
    : product.trialPeriodDays;
  const effectiveStartDate = selectedPrice ? billingConfig?.startDate : product.startDate;
  const hasTrialPeriod = checkHasTrialPeriod(effectiveTrialPeriodDays);
  const hasStartDate = checkHasValidStartDate(effectiveStartDate);
  const hasSetupFee = cartCost.setupFeeAmount > 0;
  const hasCoupon = !!cartCost.coupon;
  const hasDiscount = cartCost.totalDiscountAmount > 0;
  const hasFees = !!cartCost.fees;
  const hasOrderBumps = (cartCost.lineItems || []).some(item => item.type === 'order_bump');
  const shouldShowSubtotal =
    isPaymentPlan ||
    hasTrialPeriod ||
    hasStartDate ||
    hasSetupFee ||
    hasDiscount ||
    hasFees ||
    hasOrderBumps;

  return (
    <PriceBreakdownContainer>
      <MainProductRow />
      <PrimaryOneTimeVariantRows />
      <PrimarySetupFeeRow labels={labels} itemised />
      <OrderBumpRows />

      {shouldShowSubtotal && (
        <PriceBreakdownRow type="subtotal">
          <PriceBreakdownLabel label={labels.cartSubtotalText} type="subtotal" />
          <PriceBreakdownValue type="subtotal">
            {formatPrice({
              amount: cartCost.subtotalAmount + getItemisedPrimarySetupFeeAmount(page, cartCost),
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      <PrimarySetupFeeRow labels={labels} />

      {hasCoupon && (
        <PriceBreakdownRow type="couponDiscount">
          <PriceBreakdownLabel
            label={
              cartCost.coupon.percentOff
                ? `${labels.cartCouponDiscountText} (${cartCost.coupon.percentOff}%)`
                : labels.cartCouponDiscountText
            }
            type="couponDiscount"
          />
          <PriceBreakdownValue type="couponDiscount">
            -
            {formatPrice({
              amount: cartCost.totalDiscountAmount,
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {hasTrialPeriod && (
        <PriceBreakdownRow type="trial">
          <PriceBreakdownLabel label={labels.cartTotalAfterTrialText} type="trial" />
          <PriceBreakdownValue type="trial">
            {formatPrice({
              amount:
                cartCost.recurringTotalAmount +
                (cartCost.recurringProcessingFeeContribution?.amount ?? 0),
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {hasStartDate && (
        <PriceBreakdownRow type="startDate">
          <PriceBreakdownLabel
            label={`${labels.cartTotalOnStartDateText} ${formatStartDate(effectiveStartDate)}`}
            type="startDate"
          />

          <PriceBreakdownValue type="startDate">
            {formatPrice({
              amount:
                cartCost.recurringTotalAmount +
                (cartCost.recurringProcessingFeeContribution?.amount ?? 0),
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {cartCost.fees &&
        cartCost.fees.map((fee, index) => (
          <PriceBreakdownRow key={`${fee.name}-${index}`} type="fees">
            <PriceBreakdownLabel
              label={feeLabel(fee, cartCost.currency, currencyLocale)}
              type="fees"
            />
            <PriceBreakdownValue type="fees">
              {formatPrice({
                amount: fee.amount,
                currency: cartCost.currency,
                locale: currencyLocale,
                style: 'currency',
              })}
            </PriceBreakdownValue>
          </PriceBreakdownRow>
        ))}

      <ProcessingFeeContributionRow key="processingFeeContribution" labels={labels} />

      {!cartCost.hasProration && (
        <PriceBreakdownRow type="total">
          <PriceBreakdownLabel label={labels.cartTotalDueText} type="total" />
          <PriceBreakdownValue type="total">
            {formatPrice({
              amount: cartCost.totalDueAmount,
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {cartCost.hasProration && (
        <>
          <PriceBreakdownRow type="total">
            <PriceBreakdownLabel label={labels.cartTotalDueText} type="total" />
            <PriceBreakdownValue type="total">
              {formatPrice({
                amount: cartCost.totalDueAmountBeforeProration,
                currency: cartCost.currency,
                locale: currencyLocale,
                style: 'currency',
              })}
            </PriceBreakdownValue>
          </PriceBreakdownRow>

          <PriceBreakdownRow type="proration">
            <PriceBreakdownLabel label={labels.cartTotalDueProratedAmountText} type="proration" />
            <PriceBreakdownValue type="proration">
              {formatPrice({
                amount: cartCost.totalDueAmount,
                currency: cartCost.currency,
                locale: currencyLocale,
                style: 'currency',
              })}
            </PriceBreakdownValue>
          </PriceBreakdownRow>
        </>
      )}
    </PriceBreakdownContainer>
  );
}

function TaxEnabledPriceBreakdown({ labels }) {
  const cartCost = useCartCost();
  const { page, product, paymentOption, currencyLocale } = useCheckoutState();

  const hasCoupon = !!cartCost.coupon;
  const hasBulkDiscount = cartCost.totalBulkDiscountAmount > 0;

  const fixedTaxRate = page.tax?.mode === 'fixed' ? getFixedTaxRate({ page, product }) : null;

  const pageTicketTypeIds =
    page.ticketGroups?.flatMap(group => group.ticketTypes?.map(tt => tt._id) || []) || [];
  const isTicketTypeCoupon = checkIsTicketTypeCoupon(cartCost.coupon, page._id, pageTicketTypeIds);

  return (
    <PriceBreakdownContainer>
      <MainProductRow />
      <PrimarySetupFeeRow labels={labels} itemised />
      <TicketRows />
      <OrderBumpRows />

      <PriceBreakdownRow type="subtotal">
        <PriceBreakdownLabel label={labels.cartSubtotalText} type="subtotal" />
        <PriceBreakdownValue type="subtotal">
          {formatPrice({
            amount: getBreakdownSubtotalAmount(cartCost, page),
            currency: cartCost.currency,
            locale: currencyLocale,
            style: 'currency',
          })}
        </PriceBreakdownValue>
      </PriceBreakdownRow>

      <PrimarySetupFeeRow labels={labels} />

      {hasBulkDiscount && (
        <PriceBreakdownRow type="bulkDiscount">
          <PriceBreakdownLabel label={`${labels.cartBulkDiscountText}`} type="bulkDiscount" />
          <PriceBreakdownValue type="bulkDiscount">
            -
            {formatPrice({
              amount: cartCost.totalBulkDiscountAmount,
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {hasCoupon && isTicketTypeCoupon && (
        <TicketCouponDiscountBreakdown
          lineItems={cartCost.lineItems}
          currency={cartCost.currency}
          locale={currencyLocale}
        />
      )}

      {hasCoupon && !isTicketTypeCoupon && (
        <PriceBreakdownRow type="discount">
          <PriceBreakdownLabel
            label={
              cartCost.coupon.percentOff
                ? `${labels.cartCouponDiscountText} (${cartCost.coupon.percentOff}%)`
                : labels.cartCouponDiscountText
            }
            type="couponDiscount"
          />
          <PriceBreakdownValue type="couponDiscount">
            -
            {formatPrice({
              amount: cartCost.couponDiscountAmount,
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {cartCost.fees &&
        cartCost.fees.map((fee, index) => (
          <PriceBreakdownRow key={`${fee.name}-${index}`} type="fees">
            <PriceBreakdownLabel
              label={feeLabel(fee, cartCost.currency, currencyLocale)}
              type="fees"
            />
            <PriceBreakdownValue type="fees">
              {formatPrice({
                amount: fee.amount,
                currency: cartCost.currency,
                locale: currencyLocale,
                style: 'currency',
              })}
            </PriceBreakdownValue>
          </PriceBreakdownRow>
        ))}

      {/* Bumps get a subtotal line each, but tax stays ONE figure: every line
          is taxed at the primary's rate (useCartCost gives each `primaryTaxRate`),
          so splitting it per bump repeats one rate down the breakdown. Stripe
          mode still groups by distinct rate below, which is where a genuinely
          different rate would surface. */}
      {cartCost.oneTimeTaxAmounts?.length ? (
        cartCost.oneTimeTaxAmounts?.map(taxAmount => (
          <PriceBreakdownRow key={taxAmount.displayName} type="tax">
            <PriceBreakdownLabel
              label={
                <>
                  {getTaxLabel({ name: taxAmount.displayName, rate: taxAmount, labels })}
                  <InfoTooltip>Tax is determined by billing information</InfoTooltip>:{' '}
                </>
              }
              type="tax"
            />

            <PriceBreakdownValue type="tax">
              {formatPrice({
                amount: taxAmount.amount,
                currency: cartCost.currency,
                locale: currencyLocale,
                style: 'currency',
              })}
            </PriceBreakdownValue>
          </PriceBreakdownRow>
        ))
      ) : fixedTaxRate != null ? (
        <PriceBreakdownRow type="tax">
          <PriceBreakdownLabel
            label={getTaxLabel({ name: fixedTaxRate.displayName, rate: fixedTaxRate, labels })}
            type="tax"
          />
          <PriceBreakdownValue type="tax">
            {formatPrice({
              amount: cartCost.oneTimeTaxAmount,
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      ) : (
        <PriceBreakdownRow type="tax">
          <PriceBreakdownLabel label={labels.cartTaxText} type="tax" />
          <PriceBreakdownValue type="tax">
            {formatPrice({
              amount: cartCost.oneTimeTaxAmount,
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      <ProcessingFeeContributionRow key="processingFeeContribution" labels={labels} />

      {paymentOption.type === 'partial' && (
        <PriceBreakdownRow type="dueNow">
          <PriceBreakdownLabel label={labels.cartDueNowText} type="dueNow" />
          <PriceBreakdownValue type="dueNow">
            {formatPrice({
              amount: cartCost.totalDueAmount,
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {paymentOption.type !== 'partial' && (
        <PriceBreakdownRow type="total">
          <PriceBreakdownLabel label={labels.cartTotalDueText} type="total" />
          <PriceBreakdownValue type="total">
            {formatPrice({
              amount: cartCost.totalDueAmount,
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}
    </PriceBreakdownContainer>
  );
}

function RegularPriceBreakdown({ labels }) {
  const cartCost = useCartCost();
  const { page, product, paymentOption, currencyLocale } = useCheckoutState();
  const selectedPrice = useSelectedPrice();

  const hasCoupon = !!cartCost.coupon;
  const hasDiscount = cartCost.totalDiscountAmount > 0;
  const hasBulkDiscount = cartCost.totalBulkDiscountAmount > 0;

  // PWYW is product-level on single-price products and per-price on multi-price
  // ones. Always show the subtotal for a PWYW so the customer sees the amount
  // they entered itemised, even when it equals the total with no fees.
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.payWhatYouWant)
  const isPayWhatYouWant = !!(selectedPrice
    ? selectedPrice.payWhatYouWant
    : product.payWhatYouWant);

  const pageTicketTypeIds =
    page.ticketGroups?.flatMap(group => group.ticketTypes?.map(tt => tt._id) || []) || [];
  const isTicketTypeCoupon = checkIsTicketTypeCoupon(cartCost.coupon, page._id, pageTicketTypeIds);

  const hasOrderBumps = (cartCost.lineItems || []).some(item => item.type === 'order_bump');
  // Ticket fees sit outside subtotalAmount but inside the total due, and a fee
  // the group folds into the price has nothing to itemise, so it must not
  // force the full breakdown open on its own.
  const hasSeparateTicketFees = (cartCost.lineItems || []).some(
    item => item.type === 'ticket' && item.fee && item.fee.display === 'separate',
  );

  if (
    !isPayWhatYouWant &&
    !hasOrderBumps &&
    !hasSeparateTicketFees &&
    getBreakdownSubtotalAmount(cartCost, page) === cartCost.totalDueAmount &&
    !cartCost.setupFeeAmount &&
    !hasDiscount &&
    !hasCoupon &&
    !cartCost.fees &&
    !cartCost.processingFeeContribution
  ) {
    return (
      <PriceBreakdownContainer>
        <TicketRows />

        <PriceBreakdownRow type="total">
          <PriceBreakdownLabel label={labels.cartTotalDueText} type="total" />
          <PriceBreakdownValue type="total">
            {formatPrice({
              amount: cartCost.totalDueAmount,
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      </PriceBreakdownContainer>
    );
  }

  return (
    <PriceBreakdownContainer>
      <MainProductRow />
      <PrimarySetupFeeRow labels={labels} itemised />
      <TicketRows />
      <OrderBumpRows />

      <PriceBreakdownRow type="subtotal">
        <PriceBreakdownLabel label={labels.cartSubtotalText} type="subtotal" />
        <PriceBreakdownValue type="subtotal">
          {formatPrice({
            amount: getBreakdownSubtotalAmount(cartCost, page),
            currency: cartCost.currency,
            locale: currencyLocale,
            style: 'currency',
          })}
        </PriceBreakdownValue>
      </PriceBreakdownRow>

      <PrimarySetupFeeRow labels={labels} />

      {hasBulkDiscount && (
        <PriceBreakdownRow type="bulkDiscount">
          <PriceBreakdownLabel label={`${labels.cartBulkDiscountText}`} type="bulkDiscount" />
          <PriceBreakdownValue type="bulkDiscount">
            -
            {formatPrice({
              amount: cartCost.totalBulkDiscountAmount,
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {hasCoupon && isTicketTypeCoupon && (
        <TicketCouponDiscountBreakdown
          lineItems={cartCost.lineItems}
          currency={cartCost.currency}
          locale={currencyLocale}
        />
      )}

      {hasCoupon && !isTicketTypeCoupon && (
        <PriceBreakdownRow type="discount">
          <PriceBreakdownLabel
            label={
              cartCost.coupon.percentOff
                ? `${labels.cartCouponDiscountText} (${cartCost.coupon.percentOff}%)`
                : labels.cartCouponDiscountText
            }
            type="couponDiscount"
          />
          <PriceBreakdownValue type="couponDiscount">
            -
            {formatPrice({
              amount: cartCost.couponDiscountAmount,
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {cartCost.fees &&
        cartCost.fees.map((fee, index) => (
          <PriceBreakdownRow key={`${fee.name}-${index}`} type="fees">
            <PriceBreakdownLabel
              label={feeLabel(fee, cartCost.currency, currencyLocale)}
              type="fees"
            />
            <PriceBreakdownValue type="fees">
              {formatPrice({
                amount: fee.amount,
                currency: cartCost.currency,
                locale: currencyLocale,
                style: 'currency',
              })}
            </PriceBreakdownValue>
          </PriceBreakdownRow>
        ))}

      <ProcessingFeeContributionRow key="processingFeeContribution" labels={labels} />

      {paymentOption.type === 'partial' && (
        <PriceBreakdownRow type="dueNow">
          <PriceBreakdownLabel label={labels.cartDueNowText} type="dueNow" />
          <PriceBreakdownValue type="dueNow">
            {formatPrice({
              amount: cartCost.totalDueAmount,
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {paymentOption.type !== 'partial' && (
        <PriceBreakdownRow type="total">
          <PriceBreakdownLabel label={labels.cartTotalDueText} type="total" />
          <PriceBreakdownValue type="total">
            {formatPrice({
              amount: cartCost.totalDueAmount,
              currency: cartCost.currency,
              locale: currencyLocale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}
    </PriceBreakdownContainer>
  );
}
